import { Box, ButtonProps, IconButton, Stack, Typography } from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';

import { Icon, IconName } from '@/components/Icon';
import { ModalIndicator } from '@/components/Layout/Drawer/ModalIndicator/ModalIndicator';
import { ModalIndicatorType } from '@/components/Layout/Drawer/ModalIndicator/ModalIndicator.config';

import { BottomSheetModal } from '../BottomSheetModal/BottomSheetModal';
import { ModalProps } from '../Modal';
import { ModalSize } from '../Modal.constants';

import {
  BottomSheetDialogButton,
  BottomSheetDialogButtonContainer,
  BottomSheetDialogSecondaryButton,
} from './BottomSheetDialog.styled';

type BottomSheetDialogProps = PropsWithChildren<{
  modalProps?: ModalProps;
  size?: ModalSize;
  onClose?: () => void;
  showHeaderTitle?: boolean;
  title?: ReactNode | string;
  description?: ReactNode | string;
  drawerImage?: ReactNode;
  content?: ReactNode;
  primaryAction?: () => void;
  primaryText?: string;
  secondaryAction?: () => void;
  secondaryText?: string;
  showCloseButton?: boolean;
  indicator?: {
    type: ModalIndicatorType;
    icon: IconName;
  };
  primaryActionProps?: ButtonProps;
  secondaryActionProps?: ButtonProps;
}>;

const BottomSheetDialogHeader = ({
  showHeaderTitle,
  title,
}: Partial<BottomSheetDialogProps>) => {
  if (showHeaderTitle) {
    if (typeof title === 'string') {
      return <Typography variant="headlineH3">{title}</Typography>;
    }
    return title;
  }
  return <div />;
};

const BottomSheetModalImage = ({
  drawerImage,
  indicator,
}: Partial<BottomSheetDialogProps>) => {
  if (drawerImage && !indicator) {
    return drawerImage;
  }
  if (indicator && !drawerImage) {
    return <ModalIndicator type={indicator?.type} icon={indicator?.icon} />;
  }
  return null;
};

export function BottomSheetDialog({
  modalProps,
  onClose,
  showHeaderTitle = false,
  title,
  description,
  drawerImage,
  content,
  primaryAction,
  primaryText,
  secondaryAction,
  secondaryText,
  showCloseButton = false,
  indicator,
  primaryActionProps,
  secondaryActionProps,
}: Readonly<BottomSheetDialogProps>) {
  return (
    <BottomSheetModal onClose={onClose} modalProps={modalProps}>
      <Stack alignItems="flex-start" justifyContent="space-between" mb={1}>
        <BottomSheetDialogHeader
          showHeaderTitle={showHeaderTitle}
          title={title}
        />
        {showCloseButton ? (
          <Box mt={-0.5} mr={-0.5}>
            <IconButton onClick={onClose}>
              <Icon name="close" color={theme => theme.palette.neutral[700]} />
            </IconButton>
          </Box>
        ) : (
          <div />
        )}
      </Stack>
      <BottomSheetModalImage drawerImage={drawerImage} indicator={indicator} />
      {!showHeaderTitle && title && (
        <Typography
          variant="headlineH3"
          textAlign="center"
          mt={3}
          mb={1}
          color="neutral.1000"
        >
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="paragraphP2" color="neutral.800" mb={4}>
          {description}
        </Typography>
      )}
      {content}
      <BottomSheetDialogButtonContainer>
        {secondaryAction && (
          <BottomSheetDialogSecondaryButton
            variant="outlined"
            onClick={secondaryAction}
            {...secondaryActionProps}
          >
            {secondaryText}
          </BottomSheetDialogSecondaryButton>
        )}
        {primaryAction && (
          <BottomSheetDialogButton
            variant="contained"
            onClick={primaryAction}
            {...primaryActionProps}
          >
            {primaryText}
          </BottomSheetDialogButton>
        )}
      </BottomSheetDialogButtonContainer>
    </BottomSheetModal>
  );
}
